import { 
  DocumentTextIcon, 
  CheckCircleIcon,
  LockClosedIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  DocumentIcon,
  CogIcon,
  TicketIcon
} from '@heroicons/react/24/outline';
import type { Contract, ContractTemplate } from '@shared/types';
import { useTemplates } from '../../hooks/useTemplates';

interface TemplateStepProps {
  data: Partial<Contract> & { selectedTemplate?: ContractTemplate };
  onUpdate: (updates: Partial<Contract> & { selectedTemplate?: ContractTemplate }) => void;
}

const templateTypeIcons: Record<string, { icon: any; color: string; iconColor: string }> = {
  'SOW': { icon: ClipboardDocumentListIcon, color: 'bg-blue-50 border-blue-200', iconColor: 'text-blue-600' },
  'NDA': { icon: LockClosedIcon, color: 'bg-purple-50 border-purple-200', iconColor: 'text-purple-600' },
  'PSA': { icon: UserGroupIcon, color: 'bg-green-50 border-green-200', iconColor: 'text-green-600' },
  'MSA': { icon: DocumentTextIcon, color: 'bg-orange-50 border-orange-200', iconColor: 'text-orange-600' },
  'Service Agreement': { icon: CogIcon, color: 'bg-cyan-50 border-cyan-200', iconColor: 'text-cyan-600' },
  'License Agreement': { icon: TicketIcon, color: 'bg-pink-50 border-pink-200', iconColor: 'text-pink-600' },
  'Other': { icon: DocumentIcon, color: 'bg-gray-50 border-gray-200', iconColor: 'text-gray-600' },
};

export function TemplateStep({ data, onUpdate }: TemplateStepProps) {
  const { templates, loading } = useTemplates();

  const handleSelectTemplate = (template: ContractTemplate) => {
    onUpdate({
      selectedTemplate: template,
      templateId: template.id,
      templatePath: template.sharepointPath,
    });
  };

  const selectedTemplateId = data.selectedTemplate?.id;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Select Contract Template</h2>
        <p className="mt-2 text-gray-600">
          Choose a template that matches your contract type
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12">
          <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Templates Available</h3>
          <p className="text-gray-600">
            Contact your administrator to add contract templates.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates
            // ACORD and W9 templates aren't contract templates — ACORD is
            // generated per-client; W9 is a static email attachment.
            .filter((t) => t.templateType !== 'ACORD' && t.templateType !== 'W9')
            .map((template) => {
            const isSelected = selectedTemplateId === template.id;
            const typeConfig = templateTypeIcons[template.templateType] || templateTypeIcons['Other'];

            return (
              <div
                key={template.id}
                onClick={() => handleSelectTemplate(template)}
                className={`relative p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  isSelected
                    ? 'border-primary-600 bg-primary-50 shadow-md'
                    : `${typeConfig.color} hover:shadow-sm border-2`
                }`}
              >
                {isSelected && (
                  <div className="absolute top-3 right-3">
                    <CheckCircleIcon className="w-6 h-6 text-primary-600" />
                  </div>
                )}

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <typeConfig.icon className={`w-10 h-10 ${typeConfig.iconColor}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{template.description}</p>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="px-2 py-0.5 bg-white border border-gray-300 rounded">
                          {template.templateType}
                        </span>
                        <span className="text-gray-500">v{template.version}</span>
                      </div>

                      <div className="flex flex-wrap gap-2 mt-2">
                        {template.hasScope && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                            Scope Required
                          </span>
                        )}
                        {template.hasDeliverables && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                            Deliverables
                          </span>
                        )}
                        {template.hasMilestones && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                            Milestones
                          </span>
                        )}
                        {template.hasPaymentSchedule && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                            Payments
                          </span>
                        )}
                        {template.supportsRecurringPayments && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs bg-pink-100 text-pink-700 rounded">
                            Recurring
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.selectedTemplate && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            <p className="text-sm font-medium text-green-900">
              Template selected: {data.selectedTemplate.name}
            </p>
          </div>
          <p className="text-sm text-green-700 mt-1 ml-7">
            The wizard will adapt based on this template's requirements.
          </p>
        </div>
      )}
    </div>
  );
}

